import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AuthenticationApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';

const getClient = () => {
  return new AuthenticationApi(getApiConfiguration());
};

const MagicLinkRequest = () => {
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string[]}>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setFieldErrors({});
    setGeneralErrors([]);

    const client = getClient();

    try {
      await client.apiAuthMagicLinkGenerateCreate({
        magicLinkRequest: { email }
      });

      setSuccessMessage('Magic link has been sent to your email. Please check your inbox.');
      setGeneralErrors([]);
      setFieldErrors({});
    } catch (error: any) {
      console.error('Error during magic link request:', error);
      setSuccessMessage('');
      
      if (error.response) {
        try {
          const errorData = await error.response.json();
          const newFieldErrors: {[key: string]: string[]} = {};
          const newGeneralErrors: string[] = [];

          if (typeof errorData === 'string') {
            newGeneralErrors.push(errorData);
          } else {
            Object.entries(errorData).forEach(([key, errors]) => {
              const errorList = Array.isArray(errors) ? errors : [errors];
              if (key === 'email') {
                newFieldErrors[key] = errorList.map(err => `${err}`);
              } else if (key === 'detail') {
                errorList.forEach(err => {
                  newGeneralErrors.push(`${err}`);
                });
              }
            });
          }

          setFieldErrors(newFieldErrors);
          setGeneralErrors(newGeneralErrors);
        } catch (jsonError) {
          setGeneralErrors(["An unexpected error occurred. Please try again."]);
        }
      } else {
        setGeneralErrors(["Network error. Please check your connection and try again."]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-8">
            <Shield className="h-10 w-10 text-blue-500" />
            <h1 className="text-2xl font-bold text-white ml-3">Magic Link Login</h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 bg-gray-800/30 border ${
                    fieldErrors.email ? 'border-red-500' : 'border-gray-800'
                  } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  placeholder="Enter your email"
                  required
                />
              </div>
              {fieldErrors.email && (
                <div className="text-sm text-red-500">
                  {fieldErrors.email.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="text-sm text-green-500 bg-green-500/10 px-4 py-2 rounded-lg">
                {successMessage}
              </div>
            )}

            {/* General Errors */}
            {generalErrors.length > 0 && (
              <div className="text-sm text-red-500 bg-red-500/10 px-4 py-2 rounded-lg">
                {generalErrors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}

            {/* Submit Button */}
            <Button 
              className="w-full" 
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Sending Magic Link...' : 'Send Magic Link'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>

          {/* Footer */}
          <p className="text-sm text-gray-400 text-center mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-blue-500 hover:text-blue-400">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MagicLinkRequest;
